/**
 * UploadFileButton 组件
 *
 * 结合了 FileInput 和 FileInputButton 的优点：
 * 1. 使用类组件管理 Uppy 插件的生命周期，更好地处理插件的安装和卸载
 * 2. 提供自定义 UI，支持自定义图标和样式
 * 3. 支持文件类型限制、多文件选择等高级功能
 * 4. 与 Jotai 状态管理系统集成
 */
import React from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { enqueueFilesAtom, uppyInstanceAtom } from '../../../../atoms/v3/uppyAtoms';
import { createStandaloneToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { validateFileTypeAndSize } from '../../../../utils/fileValidation';
import { trackEvent } from '../../../../lib/analytics';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
// 移除对 messageSendingAtom 的导入，允许在发送消息时上传文件

// 创建独立的 toast 实例
const { toast } = createStandaloneToast();

interface FileUploadButtonProps {
  isDisabled?: boolean;
  className?: string;
  ariaLabel?: string;
  onClick?: () => void; // quickbar 模式下的点击回调
}

export const FileUploadButton: React.FC<FileUploadButtonProps> = ({
  isDisabled = false,
  className = "",
  ariaLabel = "添加附件",
  onClick
}) => {
  const uppy = useAtomValue(uppyInstanceAtom);
  const enqueueFiles = useSetAtom(enqueueFilesAtom);
  const { t } = useTranslation(['v3/chat', 'common']);
  const isDark = useAtomValue(isDarkAtom);
  // 移除 isSending 状态，允许在发送消息时上传文件

  const handleClick = () => {
    // 如果有外部 onClick 回调（quickbar 模式），优先执行
    if (onClick) {
      onClick();
      return;
    }

    if(!uppy){
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = uppy.opts.restrictions.maxNumberOfFiles !== 1 ;
    input.accept = uppy.opts.restrictions.allowedFileTypes?.join(', ') || '*';
    input.className="hidden"
    input.onchange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const files = target.files;
      if (files && files.length > 0) {
        // 处理每个文件，先验证再添加到队列
        const validFiles: File[] = [];

        Array.from(files).forEach((file: File) => {
          try {
            // 手动验证文件，而不是依赖Uppy的onBeforeFileAdded
            validateFileTypeAndSize({
              type: file.type,
              size: file.size,
              name: file.name
            });

            // 验证通过，添加到有效文件列表
            validFiles.push(file);
          } catch (error) {
            // 显示错误通知
            const errorMessage = error instanceof Error ? error.message : '';
            if (errorMessage.includes('不支持的文件类型')) {
              // 记录用户尝试上传不支持的文件类型
              const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'unknown';
              const fileType = file.type || 'unknown';

              // 发送埋点事件
              trackEvent('file upload unsupported type', {
                file_extension: fileExtension,
                file_type: fileType,
                file_size: file.size,
                error_message: errorMessage
              });

              // 显示友好的错误提示
              toast({
                title: t('common:errors.invalidFileType'),
                description: t('common:errors.invalidFileTypeDescription'),
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top'
              });
            } else {
              // 记录其他文件上传错误
              trackEvent('file upload error', {
                error_type: 'validation',
                error_message: errorMessage || 'unknown error'
              });

              toast({
                title: t('common:errors.fileUploadFailed'),
                description: errorMessage || t('common:errors.unknown'),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top'
              });
            }
            console.error('文件验证错误:', error);
          }
        });

        // 只将有效文件添加到队列
        if (validFiles.length > 0) {
          // 记录成功验证的文件类型
          validFiles.forEach(file => {
            const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'unknown';
            trackEvent('file upload valid type', {
              file_extension: fileExtension,
              file_type: file.type || 'unknown',
              file_size: file.size
            });
          });

          // 将File对象转换为MinimalRequiredUppyFileType
          const uppyFiles = validFiles.map(file => ({
            name: file.name,
            type: file.type,
            data: file,
            meta: {
              name: file.name,
              type: file.type
            }
          }));
          enqueueFiles(uppyFiles);
        }
      }
    };
    input.click();
  }

  return (
    <button
      disabled={isDisabled} // 移除 isSending 条件，只根据传入的 isDisabled 参数决定是否禁用
      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
        isDark
          ? 'bg-ios-dark-gray4 hover:bg-ios-dark-gray3'
          : 'bg-gray-100 hover:bg-gray-200'
      } ${className}`}
      aria-label={ariaLabel}
      onClick={handleClick}
    >
      <svg
        className={`w-5 h-5 ${isDark ? 'text-ios-dark-gray1' : 'text-gray-600'}`}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="17,8 12,3 7,8"/>
        <line x1="12" y1="3" x2="12" y2="15"/>
      </svg>
    </button>
  )
};